import React from 'react'
import { Alert, Box, Button, Link, Skeleton, Stack, Typography } from '@common/components'
import { KeyboardArrowDownIcon } from '@common/icons'
import { useRevisionListRecordingsQuery } from '@app/services/auto-generated/revision'
import errorHandler from '@common/utils/error-handler'
import dayjs from 'dayjs'
import { formatSeconds } from '@common/utils/time'
import { SessionDisplayCard } from './display-sessions'
import { useAppDispatch, useAppSelector } from '@common/hooks'
import {
  selectnewRecordingFetching,
  selectTutorialVideoModalOpen,
  setNewRecordingFetching,
  setTutorialVideoModalOpen,
} from '@features/recordings/recordings.slice'

const CompletedRecordingView = () => {
  const dispatch = useAppDispatch()
  const [dateDesc, setDateDesc] = React.useState(true)
  const [limit, setLimit] = React.useState(10)
  const {
    data: completedSessions,
    isLoading,
    isError,
    error,
    isSuccess,
    isFetching,
  } = useRevisionListRecordingsQuery(
    { limit: limit },
    { refetchOnMountOrArgChange: true, pollingInterval: 5000 },
  )
  const handleLimit = () => {
    setLimit(limit + 10)
  }
  const isNewRecordingFetching = useAppSelector(selectnewRecordingFetching)
  const prevRecordingIdRef = React.useRef<number | null>(null)
  const wasEmptyRef = React.useRef<boolean>(true)
  React.useEffect(() => {
    if (wasEmptyRef.current && completedSessions && completedSessions?.length > 0) {
      dispatch(setNewRecordingFetching(false))
      wasEmptyRef.current = false
      return
    }

    wasEmptyRef.current = !completedSessions?.length

    if (isNewRecordingFetching && !prevRecordingIdRef.current && completedSessions?.[0]) {
      prevRecordingIdRef.current = completedSessions[0].id
    }
    if (isNewRecordingFetching && prevRecordingIdRef.current && completedSessions?.[0]) {
      if (prevRecordingIdRef.current !== completedSessions[0].id) {
        dispatch(setNewRecordingFetching(false))
        prevRecordingIdRef.current = null
      }
    }
  }, [completedSessions, isNewRecordingFetching])

  return (
    <Box id={'completed-recordings-bar'} sx={{ pb: 2 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography sx={{ fontWeight: '600', marginTop: '10px' }}>Completed recordings</Typography>
        <Button color='neutral' onClick={() => setDateDesc(!dateDesc)}>
          <KeyboardArrowDownIcon
            sx={{
              transform: dateDesc ? '' : 'rotate(180deg)',
              transition: 'transform 0.2s ease-in-out',
            }}
          />
          date
        </Button>
      </Stack>
      {isLoading ? (
        <Skeleton variant='rectangular' height={80} sx={{ mt: 2 }} />
      ) : isError ? (
        <Alert severity='error' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
          {errorHandler(error)}
        </Alert>
      ) : isSuccess ? (
        completedSessions?.length === 0 ? (
          isNewRecordingFetching ? (
            <Skeleton variant='rectangular' height={80} sx={{ mt: 2 }} />
          ) : (
            <Alert severity='info' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
              <Typography variant='body1'>
                Watch this{' '}
                <Link
                  sx={{ cursor: 'pointer' }}
                  underline='always'
                  color='secondary'
                  onClick={() => dispatch(setTutorialVideoModalOpen(true))}
                >
                  video
                </Link>{' '}
                to get started!
              </Typography>
            </Alert>
          )
        ) : (
          <>
            {/* {isNewRecordingFetching && (
              <Skeleton variant='rectangular' height={80} sx={{ mt: 2 }} />
            )} */}
            {[...completedSessions]
              ?.sort((a, b) => {
                const dateA = dayjs(a.created_at)
                const dateB = dayjs(b.created_at)
                return dateDesc ? dateB.diff(dateA) : dateA.diff(dateB)
              })
              ?.map((session, i) => {
                return (
                  <SessionDisplayCard
                    key={session.id}
                    feedbackId={session.id}
                    status={session.processing_status}
                    name='Pengguin AI'
                    tags={['English', formatSeconds(session?.duration)]}
                    date={session.created_at}
                    isRecent={i === 0}
                  />
                )
              })}
            {/* {isFetching ? (
              <Skeleton variant='rectangular' height={80} sx={{ mt: 2, borderRadius: '20px' }} />
            ) : null} */}
            {completedSessions?.length === limit ? (
              <Button
                variant='text'
                color='neutral'
                onClick={handleLimit}
                sx={{ display: 'flex', width: '120px', mx: 'auto', my: 3 }}
              >
                See more
              </Button>
            ) : (
              <Box sx={{ height: '50px' }} />
            )}
          </>
        )
      ) : null}
    </Box>
  )
}

export default CompletedRecordingView
